import { CssVarsProvider, useColorScheme } from "@mui/joy";
import { useEffect, useState } from "react";
import { useMediaQuery } from "../utils/react";
import { useTheme, useSetTheme } from "../data/grid-settings";
import { isMobile } from "../shared/hooks";

export type { Theme } from "../data/grid-settings";

const THEME_CACHE_KEY = "artis-theme-cache";

const getCachedTheme = (): "light" | "dark" | null => {
  try {
    const cached = localStorage.getItem(THEME_CACHE_KEY);
    if (cached === "light" || cached === "dark") return cached;
  } catch {}
  return null;
};

const setCachedTheme = (theme: "light" | "dark") => {
  try {
    localStorage.setItem(THEME_CACHE_KEY, theme);
  } catch {}
};

export const AppThemeProvider = ({ children, ...p }: Parameters<typeof CssVarsProvider>[0]) => {
  const { theme, fetching } = useTheme();
  const [cachedTheme] = useState(getCachedTheme);
  const [currentTheme, setCurrentTheme] = useState<"light" | "dark">(cachedTheme ?? theme);

  useEffect(() => {
    setCurrentTheme(theme);
    setCachedTheme(theme);
  }, [theme]);

  useEffect(() => {
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)");
    const handleTheme = () => {
      if (isMobile) {
        setCurrentTheme(prefersDark.matches ? "dark" : "light");
      }
    };
    prefersDark.addEventListener("change", handleTheme);
    return () => prefersDark.removeEventListener("change", handleTheme);
  }, []);

  useEffect(() => document.documentElement.setAttribute("data-mode", currentTheme), [currentTheme]);

  if (fetching && !cachedTheme) {
    return null;
  }

  return (
    <CssVarsProvider {...p}>
      <SyncJoyTheme theme={currentTheme} />
      {children}
    </CssVarsProvider>
  );
};

const SyncJoyTheme = ({ theme }: { theme: "light" | "dark" }) => {
  const joy = useColorScheme();
  useEffect(() => joy.setMode(theme), [theme, joy.setMode, joy]);
  return null;
};

export const useThemeMode = () => {
  const { theme } = useTheme();
  return theme;
};

export const useSetThemeMode = () => {
  return useSetTheme();
};

export const useMinScreen = (t: "sm" | "md" | "lg") => {
  const sizes = { sm: "600px", md: "900px", lg: "1024px" };
  return useMediaQuery(`(min-width: ${sizes[t]})`);
};
